import React, { useContext, useEffect } from "react";
import { format } from "date-fns";
import "./professional.css";

import { ResumeContext } from "../../../contexts/resumeContext";
import { iconMap } from "../../../utils/constants";
import { Location, Mail, Phone } from "../../Icons/Icons";

function Professional({ info }) {
  const { setColorOptions, activeColor, setActiveColor, resumeHeadings, setImgSupported } =
    useContext(ResumeContext);

  useEffect(() => {
    setColorOptions(["#377DD5", "#525C45", "#D36C45", "#E0B140"]);
    setActiveColor("#377DD5");
    setImgSupported(true)
  }, []);

  const {
    personalDetails,
    educationDetails,
    professionalDetails,
    skills,
    social,
    misc,
  } = info;

  const education = Object.values(educationDetails || {});
  const work = Object.values(professionalDetails || {});
  const userSkills = Object.values(skills || {});
  const socialLinks = Object.values(social || {});
  const { courses, hobbies, languages, references, internships, activities, projects } =
    misc || {};
  return (
    <div className="professional">
      <div className="professional__header">
        <div>
          <h1 className="professional__title" style={{ color: activeColor }}>
            {personalDetails?.firstName?.trim()}{" "}
            {personalDetails?.lastName?.trim()}
          </h1>
          <p className="professional__jobTitle">{personalDetails?.jobTitle}</p>
          <ul className={"professional__contactList"}>
            {personalDetails?.phone && (
              <li>
                <a href={`tel:${personalDetails?.phone?.trim()}`}>
                  <Phone style={{ color: activeColor }} />
                  {personalDetails?.phone?.trim()}
                </a>
              </li>
            )}
            {(personalDetails?.address?.trim() ||
              personalDetails?.postalCode?.trim()) && (
              <li>
                <Location style={{ color: activeColor }} />
                {personalDetails?.address?.trim()}
                {personalDetails?.postalCode?.trim() && " - "}
                {personalDetails?.postalCode?.trim()}
              </li>
            )}
            {personalDetails?.email && (
              <li>
                <a href={`mailto:${personalDetails?.email?.trim()}`}>
                  <Mail style={{ color: activeColor }} />
                  {personalDetails?.email?.trim()}
                </a>
              </li>
            )}
            {socialLinks.map((link, ix) =>
              link.link !== "" ? (
                <li>
                  <a href={link.link} target="_blank">
                    <span style={{ color: activeColor }}>
                      {iconMap[link.label?.toLowerCase()] || link.label}
                    </span>
                    {link.link}
                  </a>
                </li>
              ) : null
            )}
          </ul>
        </div>
        {personalDetails?.userImage && (
          <img className="professional__userimg" src={personalDetails?.userImage} />
        )}
      </div>
      <div className="professional__body">
        {personalDetails?.summary && <div className="professional__section">
          <span class="professional__vr" style={{ background: activeColor }}></span>
          <div className="professional__section__heading" style={{ color: activeColor }}>
            <span
              class="professional__circle"
              style={{ borderColor: activeColor }}
            ></span>
            {resumeHeadings?.SUM}
          </div>
          <div className="professional__section__content">
            <p dangerouslySetInnerHTML={{ __html: personalDetails?.summary }} />
          </div>
        </div>}
        {!!userSkills?.length && <div className="professional__section">
          <span class="professional__vr" style={{ background: activeColor }}></span>
          <h3 className="professional__section__heading" style={{ color: activeColor }}>
            <span
              class="professional__circle"
              style={{ borderColor: activeColor }}
            ></span>
            {resumeHeadings?.SK}
          </h3>
          <ul className="professional__skills__list">
            {userSkills.map((skill, ix) => (
              <li key={skill + ix} style={{ background: activeColor }}>
                {skill}
              </li>
            ))}
          </ul>
        </div>}
        {!!work?.length && <div className="professional__section">
          <span class="professional__vr" style={{ background: activeColor }}></span>
          <h3 className="professional__section__heading" style={{ color: activeColor }}>
            <span
              class="professional__circle"
              style={{ borderColor: activeColor }}
            ></span>
            {resumeHeadings?.WE}
          </h3>
          {work.map((exp, ix) => (
            <div className="professional__subsection" key={`exp_${ix}`}>
              <strong>{exp.jobTitle}</strong>
              <p className="professional__subsection_text">
                {exp.company}
                {exp.location && <>, {exp.location}</>}
                <span style={{ float: "right" }}>
                  {exp.startDate && (
                    <>{format(new Date(exp.startDate), "MMM yyyy")}</>
                  )}{" "}
                  {exp.endDate && (
                     <>- {exp.endDate === "Present" ? "Present" : format(new Date(exp.endDate), "MMM yyyy")}</>
                  )}
                </span>
              </p>
              <div
                className="professional__subsection_text"
                dangerouslySetInnerHTML={{ __html: exp.description }}
              />
            </div>
          ))}
        </div>}
        {!!internships?.length && (
          <div className="professional__section">
            <span class="professional__vr" style={{ background: activeColor }}></span>
            <h3
              className="professional__section__heading"
              style={{ color: activeColor }}
            >
              <span
                class="professional__circle"
                style={{ borderColor: activeColor }}
              ></span>
              {resumeHeadings?.INT}
            </h3>
            {internships.map((intern, ix) => (
              <div className={"professional__subsection"} key={`intern_${ix}`}>
                <p style={{ marginBottom: 0 }}>
                  <strong>{intern.jobTitle}</strong>
                  <span style={{ float: "right", fontWeight: 400 }}>
                    {intern.startDate && (
                      <>{format(new Date(intern.startDate), "MMM yyyy")}</>
                    )}{" "}
                    {intern.endDate && (
                      <>- {format(new Date(intern.endDate), "MMM yyyy")}</>
                    )}
                  </span>
                </p>
                <p className={"professional__subsection_text"}>
                  {intern.company}
                  {intern.location && <>, {intern.location}</>}
                </p>
                <div
                  className={"professional__subsection_text"}
                  dangerouslySetInnerHTML={{ __html: intern.description }}
                />
              </div>
            ))}
          </div>
        )}
        {!!education?.length && <div className="professional__section">
          <span class="professional__vr" style={{ background: activeColor }}></span>
          <h3 className="professional__section__heading" style={{ color: activeColor }}>
            <span
              class="professional__circle"
              style={{ borderColor: activeColor }}
            ></span>
            {resumeHeadings?.AD}
          </h3>
          {education?.map((ed, ix) => (
            <div className={"professional__subsection"} key={`ed_${ix}`}>
              <strong>{ed.degree}</strong>
              <p className={"professional__subsection_text"}>
                {ed.institute}
                {ed.location && <>, {ed.location}</>}
                <span style={{ float: "right" }}>
                  {ed.startDate && (
                    <>{format(new Date(ed.startDate), "MMM yyyy")}</>
                  )}{" "}
                  {ed.endDate && (
                    <>- {format(new Date(ed.endDate), "MMM yyyy")}</>
                  )}
                </span>
              </p>
              <div
                className={"professional__subsection_text"}
                dangerouslySetInnerHTML={{ __html: ed.description }}
              />
            </div>
          ))}
        </div>}
        {!!projects?.length && <div className="professional__section">
          <span class="professional__vr" style={{ background: activeColor }}></span>
          <h3 className="professional__section__heading" style={{ color: activeColor }}>
            <span
              class="professional__circle"
              style={{ borderColor: activeColor }}
            ></span>
            {resumeHeadings?.PRO}
          </h3>
          {projects?.map((pro, ix) => (
            <div className={"professional__subsection"} key={`pro_${ix}`}>
              <strong>{pro.title}</strong>
              <p className={"professional__subsection_text"}>
                {pro.company}
                <span style={{ float: "right" }}>
                  {pro.startDate && (
                    <>{format(new Date(pro.startDate), "MMM yyyy")}</>
                  )}{" "}
                  {pro.endDate && (
                    <>- {format(new Date(pro.endDate), "MMM yyyy")}</>
                  )}
                </span>
              </p>
              <div
                className={"professional__subsection_text"}
                dangerouslySetInnerHTML={{ __html: pro.description }}
              />
            </div>
          ))}
        </div>}
        {!!courses?.length && (
          <div className="professional__section">
            <span class="professional__vr" style={{ background: activeColor }}></span>
            <h3
              className="professional__section__heading"
              style={{ color: activeColor }}
            >
              <span
                class="professional__circle"
                style={{ borderColor: activeColor }}
              ></span>
              {resumeHeadings?.CR}
            </h3>
            {courses?.map((course, ix) => (
              <div className={"professional__subsection"} key={`course_${ix}`}>
                <strong>{course.name}</strong>
                <p className={"professional__subsection_text"}>
                  {course.institution}{" "}
                  <span style={{ float: "right" }}>
                    {course.startDate && (
                      <>{format(new Date(course.startDate), "MMM yyyy")}</>
                    )}{" "}
                    {course.endDate && (
                      <>- {format(new Date(course.endDate), "MMM yyyy")}</>
                    )}
                  </span>
                </p>
              </div>
            ))}
          </div>
        )}
        {!!activities?.length && (
          <div className="professional__section">
            <span class="professional__vr" style={{ background: activeColor }}></span>
            <h3
              className="professional__section__heading"
              style={{ color: activeColor }}
            >
              <span
                class="professional__circle"
                style={{ borderColor: activeColor }}
              ></span>
              {resumeHeadings?.ACT}
            </h3>
            {activities.map((activity, ix) => (
              <div className={"professional__subsection"} key={`activity_${ix}`}>
                <strong>{activity.title}</strong>
                <p className={"professional__subsection_text"}>
                  {activity.institution}
                  <span style={{ float: "right" }}>
                    {activity.startDate && (
                      <>{format(new Date(activity.startDate), "MMM yyyy")}</>
                    )}{" "}
                    {activity.endDate && (
                      <>- {format(new Date(activity.endDate), "MMM yyyy")}</>
                    )}
                  </span>
                </p>
                <div
                  className={"professional__subsection_text"}
                  dangerouslySetInnerHTML={{ __html: activity.description }}
                />
              </div>
            ))}
          </div>
        )}
        {!!hobbies?.length && (
          <div className="professional__section">
            <span class="professional__vr" style={{ background: activeColor }}></span>
            <div
              className="professional__section__heading"
              style={{ color: activeColor }}
            >
              <span
                class="professional__circle"
                style={{ borderColor: activeColor }}
              ></span>
              {resumeHeadings?.HOB}
            </div>
            <p className={"professional__subsection_text"}>{hobbies}</p>
          </div>
        )}
        {!!languages?.length && (
          <div className="professional__section">
            <span class="professional__vr" style={{ background: activeColor }}></span>
            <div
              className="professional__section__heading"
              style={{ color: activeColor }}
            >
              <span
                class="professional__circle"
                style={{ borderColor: activeColor }}
              ></span>
              {resumeHeadings?.LAN}
            </div>
            <p className={"professional__subsection_text"}>{languages}</p>
          </div>
        )}
        {!!references?.length && (
          <div className="professional__section">
            <span class="professional__vr" style={{ background: activeColor }}></span>
            <h3
              className="professional__section__heading"
              style={{ color: activeColor }}
            >
              <span
                class="professional__circle"
                style={{ borderColor: activeColor }}
              ></span>
              {resumeHeadings?.REF}
            </h3>
            {references?.map((ref, ix) => (
              <div className="professional__subsection" key={`ref_${ix}`}>
                <strong>{ref.name}</strong>{" "}
                {ref.institution && (
                  <>
                    from <strong>{ref.institution}</strong>
                  </>
                )}
                <p className="professional__subsection_text">
                  {ref.email} {ref.phone && <> &nbsp; | &nbsp; {ref.phone}</>}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default Professional;
